import { memo, PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import { useCalendarGridStore, useNonReactiveStore } from '../store/calendar-grid.store';
import { useNonReactiveZoomStore, useZoomStore } from '../store/zoom.store';
import { CalendarEvent, CalendarGridDragStyles } from '../types';
import { getEffectiveDuration } from '../utils';
import { CalendarDragStateContext, defaultCalendarDragState } from './calendar-drag-state-context';
import { EventDragHandle, eventDragHandleContainerStyle } from './column-events/event-drag-handle';
import { EVENT_CSS_MAP, EventCardContent } from './column-events/event-renderer';

const MIN_DRAG_DISTANCE = 5;

/**
 * Provides a DndContext for event card drag-and-drop.
 * Drop target detection uses `document.elementsFromPoint` against `data-column-id` attributes
 * rather than @dnd-kit droppable registration, so it does not conflict with the nested
 * column-reorder DndContext inside ColumnGroupSections.
 */
const EventDragDropContextComponent = ({ children }: PropsWithChildren) => {
  const isEventDragEnabled = useCalendarGridStore((s) => s.isEventDragEnabled);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const nonReactiveStore = useNonReactiveStore();
  const nonReactiveZoomStore = useNonReactiveZoomStore();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: MIN_DRAG_DISTANCE } }));

  const handleDragStart = ({ active }: DragStartEvent) => {
    const activeId = active.id as string;
    setActiveDragId(activeId);

    // Find which group the dragged event's source column belongs to so we can
    // later reject drops onto columns in a different group.
    const { groupIds, groupsById, columnsById, setActiveDragSourceGroupId, onEventDragStartRef } =
      nonReactiveStore.getState();
    const sourceGroupId =
      groupIds.find((gId) =>
        groupsById[gId]?.columnIds.some((cId) => columnsById[cId]?.[gId]?.eventIds.includes(activeId))
      ) ?? null;
    setActiveDragSourceGroupId(sourceGroupId);

    onEventDragStartRef.current?.(activeId);
  };

  const handleDragEnd = ({ active }: DragEndEvent) => {
    setActiveDragId(null);

    const {
      onEventDragEndRef,
      dragSnapMinutes,
      setEventDragPreview,
      clearEventDragPreview,
      activeDragSourceGroupId,
      setActiveDragSourceGroupId,
    } = nonReactiveStore.getState();

    // Always clear the source-group restriction when drag ends.
    setActiveDragSourceGroupId(null);

    const { pxPerMinute } = nonReactiveZoomStore.getState();
    if (!onEventDragEndRef.current) return;

    const translated = active.rect.current.translated;
    if (!translated) return;

    // Use the center of the dragged card to locate the drop column.
    // elementsFromPoint returns all elements top-to-bottom; the dragged card itself
    // has opacity: 0 but is still in the DOM, so we look past it with `find`.
    const cx = translated.left + translated.width / 2;
    const cy = translated.top + translated.height / 2;

    const elements = document.elementsFromPoint(cx, cy);
    const columnEl = elements.find((el) => (el as HTMLElement).dataset?.columnId) as HTMLElement | undefined;
    if (!columnEl) return;

    const { columnId, groupId } = columnEl.dataset as { columnId?: string; groupId?: string };
    if (!columnId || !groupId) return;

    // Reject the drop if it targets a different group than the source.
    if (activeDragSourceGroupId !== null && groupId !== activeDragSourceGroupId) return;

    const columnRect = columnEl.getBoundingClientRect();
    const relativeY = translated.top - columnRect.top;
    const rawMinutes = relativeY / pxPerMinute;
    const snap = dragSnapMinutes ?? 5;
    const snappedMinutes = Math.round(rawMinutes / snap) * snap;
    const clampedMinutes = Math.max(0, Math.min(snappedMinutes, 24 * 60 - 1));

    // Keep the drop-target overlay visible at the dropped position while a confirmation
    // modal may be shown. The consumer clears it by calling the clearPreview argument
    // once the drop is confirmed or cancelled.
    setEventDragPreview({ columnId, groupId, startMinutes: clampedMinutes, eventId: active.id as string });

    onEventDragEndRef.current(active.id as string, columnId, groupId, clampedMinutes, clearEventDragPreview);
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    const { setEventDragPreview, setActiveDragSourceGroupId } = nonReactiveStore.getState();
    setEventDragPreview(null);
    setActiveDragSourceGroupId(null);
  };

  const handleDragMove = ({ active }: DragMoveEvent) => {
    const translated = active.rect.current.translated;
    if (!translated) return;

    const cx = translated.left + translated.width / 2;
    const cy = translated.top;

    const elements = document.elementsFromPoint(cx, cy);
    const columnEl = elements.find((el) => (el as HTMLElement).dataset?.columnId) as HTMLElement | undefined;
    if (!columnEl) {
      nonReactiveStore.getState().setEventDragPreview(null);
      return;
    }

    const { columnId, groupId } = columnEl.dataset as { columnId?: string; groupId?: string };
    if (!columnId || !groupId) return;

    const { dragSnapMinutes, activeDragSourceGroupId } = nonReactiveStore.getState();

    // Suppress the drop preview when hovering over a column in a different group.
    // This also keeps useDroppable's isOver false for those columns via the disabled prop in ColumnBody.
    if (activeDragSourceGroupId !== null && groupId !== activeDragSourceGroupId) {
      nonReactiveStore.getState().setEventDragPreview(null);
      return;
    }
    const { pxPerMinute } = nonReactiveZoomStore.getState();
    const columnRect = columnEl.getBoundingClientRect();
    const relativeY = translated.top - columnRect.top;
    const rawMinutes = relativeY / pxPerMinute;
    const snap = dragSnapMinutes ?? 5;
    const snappedMinutes = Math.round(rawMinutes / snap) * snap;
    const startMinutes = Math.max(0, Math.min(snappedMinutes, 24 * 60 - 1));

    nonReactiveStore.getState().setEventDragPreview({ columnId, groupId, startMinutes, eventId: active.id as string });
  };

  if (!isEventDragEnabled) return <>{children}</>;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ layoutShiftCompensation: false }}
    >
      {children}
      {createPortal(
        // z-index must exceed EVENT_Z_INDICES max (60) and CALENDAR_Z_INDICES max (100)
        <DragOverlay style={{ zIndex: 200 }} dropAnimation={null}>
          {activeDragId && <EventDragOverlayContent eventId={activeDragId} />}
        </DragOverlay>,
        document.body
      )}
      {!activeDragId && <PendingDropDragOverlay />}
    </DndContext>
  );
};

export const EventDragDropContext = memo(EventDragDropContextComponent);

const EventDragOverlayContent = ({
  eventId,
  isPendingDropOverlay = false,
}: {
  eventId: string;
  isPendingDropOverlay?: boolean;
}) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);

  if (!eventInfo) return null;

  const resolvedEventDragOverlay =
    typeof dragStyles.eventDragOverlay === 'function'
      ? (dragStyles.eventDragOverlay as (e: typeof eventInfo) => Interpolation<Theme>)(eventInfo)
      : dragStyles.eventDragOverlay;

  const eventDragOverlayStyle: Interpolation<Theme> = [
    { opacity: 0.85, pointerEvents: 'none' },
    overlayContentStyle,
    defaultEventDragOverlayStyle(isPendingDropOverlay),
    resolvedEventDragOverlay,
  ];

  return (
    <div css={[placeholderStyle, dragOverlayWrapperStyle, eventDragHandleContainerStyle(true)]}>
      <CalendarDragStateContext.Provider
        value={{ ...defaultCalendarDragState, isEventDragOverlay: true, eventDragOverlayStyle }}
      >
        <EventDragHandle css={eventDragOverlayStyle} />
        <EventCardContent eventInfo={eventInfo} />
      </CalendarDragStateContext.Provider>
    </div>
  );
};

/**
 * Renders the dragged event card statically at the drop position after drag ends,
 * while a confirmation modal is open. Uses eventDragPreview (kept alive in the store)
 * to locate the target column via DOM query and compute absolute screen coordinates.
 */
const PendingDropDragOverlay = () => {
  const eventDragPreview = useCalendarGridStore((s) => s.eventDragPreview);
  const eventsById = useCalendarGridStore((s) => s.eventsById);
  const pxPerMinute = useZoomStore((s) => s.pxPerMinute);
  const positionRef = useRef<{ left: number; top: number; width: number } | null>(null);
  const [, forceUpdate] = useState(0);

  useLayoutEffect(() => {
    if (!eventDragPreview) {
      positionRef.current = null;
      return;
    }

    const columnEl = document.querySelector(`[data-column-id="${eventDragPreview.columnId}"]`) as HTMLElement | null;

    if (!columnEl) return;

    // The grid container scrolls vertically — find the nearest scrollable ancestor to
    // account for scroll offset so the overlay lands at the correct visual position.
    const scrollContainer = columnEl.closest('[style*="overflow"]') as HTMLElement | null;
    const scrollTop = scrollContainer?.scrollTop ?? 0;

    const rect = columnEl.getBoundingClientRect();
    positionRef.current = {
      left: rect.left,
      top: rect.top + eventDragPreview.startMinutes * pxPerMinute - scrollTop,
      width: rect.width,
    };
    forceUpdate((n) => n + 1);
  }, [eventDragPreview, pxPerMinute]);

  if (!eventDragPreview || !positionRef.current) return null;

  const eventInfo = eventsById[eventDragPreview.eventId];
  if (!eventInfo) return null;

  const { left, top, width } = positionRef.current;
  const height = getEffectiveDuration(eventInfo.duration) * pxPerMinute;

  return createPortal(
    <div css={[pendingDropDragOverlayStyle, { left, top, width, height }]}>
      <EventDragOverlayContent eventId={eventDragPreview.eventId} isPendingDropOverlay={true} />
    </div>,
    document.body
  );
};

interface EventDropOverlayContentProps {
  isOver: boolean;
  columnId: string;
  eventDragPreview: { columnId: string; startMinutes: number; eventId: string } | null;
  eventsById: Record<string, CalendarEvent | undefined>;
  dragStyles: CalendarGridDragStyles;
  pxPerMinute: number;
}

const EventDropOverlayContentComponent = ({
  columnId,
  eventDragPreview,
  eventsById,
  dragStyles,
  pxPerMinute,
}: EventDropOverlayContentProps) => {
  if (eventDragPreview?.columnId !== columnId || !dragStyles.eventDropOverlay) return null;

  const draggedEvent = eventsById[eventDragPreview.eventId];
  if (!draggedEvent) return null;

  const eventCss = EVENT_CSS_MAP[draggedEvent.eventType];

  const overlayStyle =
    typeof dragStyles.eventDropOverlay === 'function'
      ? (dragStyles.eventDropOverlay as (e: CalendarEvent) => Interpolation<Theme>)(draggedEvent)
      : dragStyles.eventDropOverlay;

  if (!overlayStyle) return null;

  return (
    <div
      css={[
        placeholderStyle,
        eventCss,
        {
          top: eventDragPreview.startMinutes * pxPerMinute,
          height: getEffectiveDuration(draggedEvent?.duration) * pxPerMinute,
        },
      ]}
    >
      <CalendarDragStateContext.Provider
        value={{
          ...defaultCalendarDragState,
          isEventDropOverlay: true,
          eventDropOverlayStyle: overlayStyle,
        }}
      >
        <EventCardContent eventInfo={draggedEvent} />
      </CalendarDragStateContext.Provider>
    </div>
  );
};

export const EventDropOverlayContent = memo(EventDropOverlayContentComponent);

const overlayContentStyle = css({
  width: '100%',
  height: '100%',
  '> *': {
    height: '100%',
  },
});

const defaultEventDragOverlayStyle = (isPendingDropOverlay = false) =>
  css({
    transform: 'translateX(5px) ',
    ...(!isPendingDropOverlay ? { transform: 'translateX(-15px) rotate(-10deg)' } : {}),
  });

const placeholderStyle = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
});

const dragOverlayWrapperStyle = css({
  cursor: 'grabbing',
});

const pendingDropDragOverlayStyle = css({
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 200,
  cursor: 'grabbing',
});
